import { useMutation } from '@tanstack/react-query';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { lazily } from 'react-lazily';

import {
	type PostErrorResponse,
	postSignin,
	type PostSigninRequest,
} from 'services/idm';

import { useCaseInsensitiveParam } from 'hooks/use-case-insensitive-params';
import { useAppTranslation } from 'hooks/use-translates';

import { PUBLIC_BASE_URL_ENV } from 'tools/environments';

import { ApiErrorBlock } from '@login/components/api-error-block';
import { FormNavigation } from '@login/components/form-navigation';
import { RichText } from '@login/components/rich-text';
import { apiErrorHandler } from '@login/helpers';
import { useApiRedirect } from '@login/hooks/use-api-redirect';

import styles from './signin-form.module.scss';

const Button = lazy(() => import('core/generic/elements/button'));
const { Insert } = lazily(() => import('core/generic/elements/form'));

type SigninFormPlaceholders = {
	registerUrl?: string;
	signInUrl?: string;
	forgottenPasswordUrl?: string;
};

type SigninFormProps = {
	placeholders: SigninFormPlaceholders | null;
	token?: string | null;
	userUnderAge?: boolean;
};

/** IDM's password login type. */
const PASSWORD_LOGIN_TYPE = 1;

export const SigninForm = ({
	placeholders,
	token,
	userUnderAge,
}: SigninFormProps) => {
	const { i18n } = useTranslation();
	const { t } = useAppTranslation(placeholders);
	const { goToRedirect } = useApiRedirect();
	const returnUrl = useCaseInsensitiveParam('returnUrl');

	const [apiErrorMessage, setApiErrorMessage] = useState('');
	const [continueAsName, setContinueAsName] = useState('');

	const {
		register,
		handleSubmit,
		setValue,
		setError,
		formState: { errors },
	} = useForm<PostSigninRequest>({ mode: 'onBlur' });

	useEffect(() => {
		if (userUnderAge) setApiErrorMessage(t('signin.user_under_age'));
	}, [userUnderAge, t]);

	// `mutate`, not `mutateAsync`: failures are handled in onError, and an
	// awaited rejection here would surface as an unhandled promise rejection.
	const { mutate: submitSignin, isPending: isSubmitting } = useMutation({
		mutationKey: ['signin', i18n.language, token, returnUrl],
		mutationFn: ({ username, password }: PostSigninRequest) =>
			postSignin({
				data: {
					username: username.trim(),
					password,
					returnUrl,
					loginType: PASSWORD_LOGIN_TYPE,
				},
				lang: i18n.language,
				token,
			}),
		onSuccess: (response) => {
			if (response.action === 'Redirect' && response.redirectUrl) {
				goToRedirect({
					action: response.action,
					url: response.redirectUrl,
				});
				return;
			}

			if (
				response.action === 'Display' &&
				response.flow.flowId === 'signin' &&
				response.flow.contentId === 'user-under-age'
			) {
				setApiErrorMessage(t('signin.user_under_age'));
			}
		},
		onError: (err) => {
			const error = err as unknown as PostErrorResponse;
			apiErrorHandler(error, i18n.language);
			setValue('password', '');

			if (error.title === 'INVALID_LOGIN') {
				setApiErrorMessage(t('signin.invalid_credentials_error_ec4'));
			} else if (error.title === 'USER_ALREADY_SIGNED_IN') {
				setApiErrorMessage(
					t('signin.user_already_signed_in_error', {
						name: error.detail,
					})
				);
				setContinueAsName(error.detail);
			}

			if (error.status === 422 && error.title === 'VALIDATION_ERROR') {
				if (error.errors?.Username) {
					setError('username', {
						message: error.errors.Username[0],
					});
				}
				if (error.errors?.Password) {
					setError('password', {
						message: error.errors.Password[0],
					});
				}
			}
		},
	});

	const handleOnSubmit = useCallback(
		(formData: PostSigninRequest) => {
			setApiErrorMessage('');
			submitSignin(formData);
		},
		[submitSignin]
	);

	return (
		<Suspense fallback={null}>
			<form
				onSubmit={handleSubmit(handleOnSubmit)}
				id="form-profile"
				className={styles['signin-form']}
			>
				<FormNavigation
					activeBtn="signin"
					signInUrl={placeholders?.signInUrl}
					registerUrl={placeholders?.registerUrl}
					returnUrl={returnUrl}
				/>

				<ApiErrorBlock apiErrorMessage={apiErrorMessage} />

				{!continueAsName && (
					<>
						<div className={styles['input-wrapper']}>
							<Insert
								{...register('username', {
									required: t('signin.username_required'),
								})}
								inputMode="email"
								defaultValue={''}
								tabIndex={0}
								id="username"
								label={t('signin.username_placeholder')}
								required={false}
								error={errors.username?.message}
								errorIcon="AlertCircleFilled"
							/>
							<Insert
								{...register('password', {
									required: t('signin.password_required'),
								})}
								defaultValue={''}
								tabIndex={0}
								id="password"
								label={t('signin.password_label')}
								required={false}
								type="password"
								error={errors.password?.message}
								errorIcon="AlertCircleFilled"
							/>
						</div>

						<Button
							type="button"
							href={
								placeholders?.forgottenPasswordUrl ||
								'/forgot-password'
							}
							label={t('signin.forgot_password_link')}
							variant="link"
							className="cta-underlined-medium"
						/>
						<Button
							type="submit"
							label={t('signin.signin_button')}
							isLoading={isSubmitting}
							disabled={isSubmitting}
							variant="primary"
							dimension="large"
							fullWidth={true}
						/>
					</>
				)}

				{continueAsName && (
					<>
						<Button
							type="button"
							label={t('signin.continue_as_button', {
								name: continueAsName,
							})}
							variant="primary"
							href={returnUrl || PUBLIC_BASE_URL_ENV}
							fullWidth={true}
						/>
						<Button
							type="button"
							label={t('signout.signout_button')}
							variant="secondary"
							href="/signout"
							fullWidth={true}
						/>
					</>
				)}

				<div className={`body-small medium ${styles.registerText}`}>
					<RichText
						text={t('common.register_link')}
						linkClassName={styles['register-now']}
					/>
				</div>
			</form>
		</Suspense>
	);
};

export default SigninForm;
